import React from 'react';
import { useForm } from 'react-hook-form';

import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import {
  IOffboardingFormInputs,
  IOffboardingResponse,
} from '@/interfaces/offbording.model';

interface OffboardingCompleteStepProps {
  offboardingId: string;
  employeeDetails: Partial<IOffboardingFormInputs> | null;
  onComplete: () => void;
  initialCheckboxes?: { assetHandover: boolean; isCompletedProject: boolean };
}

const OffboardingCompleteStep: React.FC<OffboardingCompleteStepProps> = ({
  offboardingId,
  employeeDetails,
  onComplete,
  initialCheckboxes = { assetHandover: false, isCompletedProject: false },
}) => {
  const isCompleted = employeeDetails?.status === 'Completed';

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<{ assetHandover: boolean; isCompletedProject: boolean }>({
    defaultValues: {
      assetHandover: initialCheckboxes.assetHandover,
      isCompletedProject: initialCheckboxes.isCompletedProject,
    },
  });

  const onSubmit = async (data: {
    assetHandover: boolean;
    isCompletedProject: boolean;
  }) => {
    if (isCompleted) return;

    try {
      const payload = {
        assetHandover: data.assetHandover,
        isCompletedProject: data.isCompletedProject,
        status: 'Completed',
      };

      const url = `${API_URL}${API_ENDPOINT.offboardUser}/${offboardingId}`;
      const response = (await callApi(url, {
        method: 'PUT',
        data: payload,
      })) as IOffboardingResponse;

      if (response?.status) {
        showToastNotification('success', 'Offboarding completed successfully');
        onComplete();
      } else {
        showToastNotification(
          'danger',
          response?.message || 'Failed to complete offboarding',
        );
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    }
  };

  return (
    <div className="row justify-content-center">
      <div className="col-lg-8 col-xl-6">
        <div className="card shadow-sm border-0">
          <div className="card-body p-4 p-md-5">
            <h4 className="card-title mb-4 d-flex align-items-center text-primary">
              <i
                className="bi bi-check2-circle me-2"
                style={{ fontSize: '1.5rem' }}
              ></i>
              {isCompleted ? 'Offboarding Completed' : 'Finalize Offboarding'}
            </h4>

            {isCompleted && (
              <div
                className="alert alert-success d-flex align-items-center"
                role="alert"
              >
                <i className="bi bi-check-circle-fill me-2"></i>
                <div>
                  This offboarding process has already been{' '}
                  <strong>Completed</strong>. No further changes are allowed.
                </div>
              </div>
            )}

            {/* Offboarding type and status */}
            <div className="row g-3 mb-4">
              <div className="col-sm-6">
                <div className="bg-light p-3 rounded-3">
                  <small className="text-muted d-block">Offboarding Type</small>
                  <strong>{employeeDetails?.offBoardType || '—'}</strong>
                </div>
              </div>
              <div className="col-sm-6">
                <div className="bg-light p-3 rounded-3">
                  <small className="text-muted d-block">Current Status</small>
                  <span
                    className={`badge fs-6 ${
                      isCompleted ? 'bg-success' : 'bg-warning text-dark'
                    }`}
                  >
                    {employeeDetails?.status || 'Initiated'}
                  </span>
                </div>
              </div>
            </div>

            <div className="row g-4 mb-4">
              <div className="col-12">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="assetHandover"
                    {...register('assetHandover')}
                    disabled={isCompleted}
                  />
                  <label
                    className={`form-check-label fw-semibold ${
                      isCompleted ? 'text-muted' : ''
                    }`}
                    htmlFor="assetHandover"
                  >
                    Asset Handover
                  </label>
                  <div className="text-muted small">
                    {isCompleted
                      ? 'All assets have been returned.'
                      : 'Confirm all company assets have been returned'}
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isCompletedProject"
                    {...register('isCompletedProject')}
                    disabled={isCompleted}
                  />
                  <label
                    className={`form-check-label fw-semibold ${
                      isCompleted ? 'text-muted' : ''
                    }`}
                    htmlFor="isCompletedProject"
                  >
                    Completed Project
                  </label>
                  <div className="text-muted small">
                    {isCompleted
                      ? 'All projects are completed or handed over.'
                      : 'All pending projects are completed or handed over'}
                  </div>
                </div>
              </div>
            </div>

            {/* Action button */}
            <div className="text-center mt-4">
              {isCompleted ? (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-lg px-5"
                  onClick={onComplete}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Go to Employees
                </button>
              ) : (
                <button
                  type="submit"
                  className="btn btn-success btn-lg px-5"
                  disabled={isSubmitting}
                  onClick={handleSubmit(onSubmit)}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Completing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Complete Offboarding
                    </>
                  )}
                </button>
              )}

              {!isCompleted && (
                <p className="text-muted small mt-3 mb-0">
                  This action will mark the offboarding process as{' '}
                  <strong>Completed</strong>.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OffboardingCompleteStep;
